import { Trans } from '@lingui/react/macro';
import Notification from './Notification';

export type OrgUnitProvisioningFailure = {
  entityName: string;
  reason?: string;
  code?: string;
};

/**
 * Reports the per-unit entities that could not be provisioned while saving an organizational unit. The
 * unit itself saved, so the API returns these as warnings on the response rather than as an error.
 *
 * AE-3010 gets its own wording: that one means the acting user holds no role on the entity's workflow
 * initial step ("Administrador Organizacional" for Reporting Requirements), which managing org units
 * does not imply.
 */
export function OrgUnitProvisioningWarning({
  warnings,
  secondsToRedirect,
}: {
  warnings?: OrgUnitProvisioningFailure[] | null;
  secondsToRedirect?: number | null;
}) {
  if (!warnings || warnings.length === 0) return null;

  const isPermissionIssue = warnings.some(w => w.code === 'AE-3010');

  return (
    <Notification color="warning">
      <h5>
        <Trans>Saved, but some components could not be created</Trans>
      </h5>
      <ul className="mb-2">
        {warnings.map((w, ix) => (
          <li key={`${w.entityName}-${ix}`}>
            <b>{w.entityName}</b>
            {w.reason ? `: ${w.reason}` : null}
          </li>
        ))}
      </ul>
      {secondsToRedirect != null && secondsToRedirect > 0 && (
        <p className="mb-2">
          <b>
            <Trans>Returning to the list in {secondsToRedirect}s.</Trans>
          </b>{' '}
          <Trans>Saving is disabled here because this unit is already created.</Trans>
        </p>
      )}
      <p className="mb-0">
        {isPermissionIssue ? (
          <Trans>
            The organizational unit was saved. Creating the components listed above requires a role on their workflow, which your account does not
            currently hold. Ask an administrator to grant it, then reopen this unit and save it to create the missing components.
          </Trans>
        ) : (
          <Trans>
            The organizational unit was saved. Reopen it and save once the cause above is resolved to create the missing components.
          </Trans>
        )}
      </p>
    </Notification>
  );
}

export default OrgUnitProvisioningWarning;
